import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Alert, Chip, Button,
  Skeleton, useTheme, alpha, Tooltip, IconButton, Autocomplete, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KingBed as KingBedIcon,
  Hotel as HotelIcon,
  EventAvailable as EventAvailableIcon,
  Block as BlockIcon,
  Bookmark as BookmarkIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Percent as PercentIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  History as HistoryIcon,
  MonitorHeart as MonitorHeartIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import PainelNaoConfigurado from '../../components/saude/PainelNaoConfigurado';

// ─── Tipos ───

interface OcupacaoTotais {
  total_leitos: number;
  ocupados: number;
  disponiveis: number;
  bloqueados: number;
  reservados: number;
  taxa_ocupacao: number;
  salas: number;
  unidades: number;
}

interface PorUnidade {
  codunidade: string;
  nome: string;
  total: number;
  ocupados: number;
  disponiveis: number;
  bloqueados: number;
  reservados: number;
  taxa_ocupacao: number;
}

interface PorSala {
  acomodacao_id: number;
  sala: string;
  bloco: string;
  clinica: string;
  unidade_nome: string;
  sexo: string | null;
  total: number;
  ocupados: number;
  disponiveis: number;
  bloqueados: number;
  reservados: number;
  taxa_ocupacao: number;
}

interface LeitoItem {
  leito_id: number;
  leito: string;
  situacao: string; // O | D | B | R
  situacao_nome: string;
  indisolamento: string | null;
  acomodacao_id: number;
  sala: string;
  bloco: string;
  unidade_nome: string;
}

interface Movimentacao {
  internacoes: number;
  altas: number;
  permanencia_media_dias: number;
}

interface OcupacaoLeitos {
  totais: OcupacaoTotais;
  por_unidade: PorUnidade[];
  por_sala: PorSala[];
  leitos: LeitoItem[];
  movimentacao: Movimentacao;
  periodo: { inicio: string; fim: string };
  gerado_em: string;
}

interface Sala {
  id_sala: number;
  nomsala: string;
}

// Detalhe de uma sala (POC 16.10 / 16.11) — pacientes internados e tempo medio de permanencia
interface PacienteLeito {
  leito_id: number;
  leito: string;
  internacao_id: number;
  numprontuario: number | null;
  nome: string | null;
  sexo: string | null; // M | F
  idade: number | null;
  dt_internacao_fmt: string;
  permanencia_seg: number;
}

interface SalaDetalhe {
  acomodacao_id: number;
  sala: string | null;
  bloco: string | null;
  clinica: string | null;
  unidade_nome: string | null;
  ocupados: number;
  permanencia_media_seg: number;
  pacientes: PacienteLeito[];
  gerado_em: string;
}

// Historico do atendimento de um paciente internado (POC 16.16) — linha do tempo anonimizada
interface SinalVital {
  rotulo: string;
  valor: string;
}

interface HistoricoEvento {
  tipo: 'admissao' | 'evolucao' | 'alta';
  data_hora_fmt: string;
  titulo: string;
  detalhes: string | null;
  sinais?: SinalVital[];
}

interface HistoricoPaciente {
  internacao_id: number;
  leito: string | null;
  numprontuario: number | null;
  nome: string | null;
  sexo: string | null;
  idade: number | null;
  dt_internacao_fmt: string | null;
  permanencia_seg: number;
  situacao: string | null; // Internado | Alta
  total_evolucoes: number;
  eventos: HistoricoEvento[];
  gerado_em: string;
}

const REFRESH_MS = 60_000;

/** Data de hoje / N dias atras no formato YYYY-MM-DD. */
const hojeISO = (): string => new Date().toISOString().slice(0, 10);
const diasAtrasISO = (dias: number): string =>
  new Date(Date.now() - dias * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

type CorMui = 'error' | 'success' | 'warning' | 'default';

/** Cor/label por situacao do leito. */
const SITUACOES: Record<string, { cor: CorMui; hex: string; label: string }> = {
  O: { cor: 'error', hex: '#e53935', label: 'Ocupado' },
  D: { cor: 'success', hex: '#2e7d32', label: 'Disponível' },
  B: { cor: 'default', hex: '#757575', label: 'Bloqueado' },
  R: { cor: 'warning', hex: '#f9a825', label: 'Reservado' },
};
const corSituacao = (s: string) => SITUACOES[s] || { cor: 'default' as CorMui, hex: '#9e9e9e', label: s };

/** Tempo de permanencia (segundos) -> texto curto: "Xd Yh" / "Yh Zmin" / "Zmin". */
const formatarPermanencia = (seg: number): string => {
  if (!seg || seg <= 0) return '—';
  const dias = Math.floor(seg / 86400);
  const horas = Math.floor((seg % 86400) / 3600);
  const min = Math.floor((seg % 3600) / 60);
  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${min}min`;
  return `${min}min`;
};

/** Label do sexo do paciente. */
const labelSexo = (s: string | null): string => (s === 'M' ? 'Masculino' : s === 'F' ? 'Feminino' : '—');

// ─── KPI Card ───

const KpiCard: React.FC<{
  label: string; value: string; icon: React.ReactNode; color: string; loading: boolean;
}> = ({ label, value, icon, color, loading }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: alpha(color, 0.12), color,
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {loading
            ? <Skeleton width={72} height={36} />
            : <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Pagina ───

const SaudeOcupacaoLeitos: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<OcupacaoLeitos | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [naoConfigurado, setNaoConfigurado] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
  const [segundos, setSegundos] = useState(REFRESH_MS / 1000);

  // Filtro Sala (lookup vazio por padrao; so e preenchido apos escolher a Unidade)
  const [salas, setSalas] = useState<Sala[]>([]);
  const [salasLoading, setSalasLoading] = useState(false);
  const [salaSelecionada, setSalaSelecionada] = useState<Sala | null>(null);

  // Filtro Periodo (movimentacao) — default ultimos 30 dias
  const [inicio, setInicio] = useState<string>(diasAtrasISO(30));
  const [fim, setFim] = useState<string>(hojeISO());

  // Detalhe da sala (POC 16.10 / 16.11) — modal com tempo medio + pacientes internados
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [salaDetalheRef, setSalaDetalheRef] = useState<PorSala | null>(null);
  const [detalhe, setDetalhe] = useState<SalaDetalhe | null>(null);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [detalheErro, setDetalheErro] = useState<string | null>(null);

  // Historico do paciente (POC 16.16) — modal aninhado com a linha do tempo do atendimento
  const [historicoRef, setHistoricoRef] = useState<PacienteLeito | null>(null);
  const [historico, setHistorico] = useState<HistoricoPaciente | null>(null);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [historicoErro, setHistoricoErro] = useState<string | null>(null);

  const fetchRef = useRef<() => void>(() => {});

  // Lookup de salas: so e preenchido quando ha uma unidade selecionada
  useEffect(() => {
    setSalaSelecionada(null);
    if (!municipioAtivo?.id || !unidadeSelecionada?.codunidade) {
      setSalas([]);
      return;
    }
    let cancelled = false;
    setSalasLoading(true);
    saudeService
      .getSalas(municipioAtivo.id, unidadeSelecionada.codunidade)
      .then((d: Sala[]) => { if (!cancelled) setSalas(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setSalas([]); })
      .finally(() => { if (!cancelled) setSalasLoading(false); });
    return () => { cancelled = true; };
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade]);

  const fetchData = useCallback(async (silencioso = false) => {
    if (!municipioAtivo?.id) return;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const result = await saudeService.getOcupacaoLeitos(municipioAtivo.id, {
        unidade: unidadeSelecionada?.codunidade,
        sala: salaSelecionada ? String(salaSelecionada.id_sala) : undefined,
        inicio,
        fim,
      });
      setNaoConfigurado(result?.configurado === false);
      setData(result);
      setUltimaAtualizacao(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar a ocupação de leitos.');
    } finally {
      setLoading(false);
      setSegundos(REFRESH_MS / 1000);
    }
  }, [municipioAtivo?.id, unidadeSelecionada?.codunidade, salaSelecionada, inicio, fim]);

  useEffect(() => { fetchRef.current = () => fetchData(true); });

  // Busca inicial / ao trocar qualquer filtro
  useEffect(() => { fetchData(false); }, [fetchData]);

  // Auto-refresh a cada 60s (silencioso) + contador regressivo
  useEffect(() => {
    if (!municipioAtivo?.id) return;
    const refresh = setInterval(() => fetchRef.current(), REFRESH_MS);
    const tick = setInterval(() => setSegundos((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [municipioAtivo?.id]);

  const totais = data?.totais;
  const mov = data?.movimentacao;
  const porUnidade = data?.por_unidade || [];
  const porSala = data?.por_sala || [];
  const leitos = data?.leitos || [];
  const mostrarUnidade = !unidadeSelecionada;

  // Agrupa os leitos (grid) por sala/acomodacao
  const leitosPorSala = useMemo(() => {
    const map = new Map<number, { sala: string; bloco: string; unidade_nome: string; itens: LeitoItem[] }>();
    leitos.forEach((l) => {
      const g = map.get(l.acomodacao_id) || { sala: l.sala, bloco: l.bloco, unidade_nome: l.unidade_nome, itens: [] };
      g.itens.push(l);
      map.set(l.acomodacao_id, g);
    });
    return Array.from(map.values());
  }, [leitos]);

  // Abre o modal de detalhe da sala e busca os pacientes internados (sob demanda).
  const abrirSala = useCallback((s: PorSala) => {
    if (!municipioAtivo?.id) return;
    setSalaDetalheRef(s);
    setDetalhe(null);
    setDetalheErro(null);
    setDetalheLoading(true);
    saudeService
      .getOcupacaoLeitosPacientes(municipioAtivo.id, { acomodacaoId: s.acomodacao_id })
      .then((d: SalaDetalhe) => setDetalhe(d))
      .catch((err: any) => setDetalheErro(err?.response?.data?.message || 'Erro ao carregar os pacientes da sala.'))
      .finally(() => setDetalheLoading(false));
  }, [municipioAtivo?.id]);

  const fecharSala = useCallback(() => {
    setSalaDetalheRef(null);
    setDetalhe(null);
    setDetalheErro(null);
  }, []);

  // Abre o modal de historico do paciente e busca a linha do tempo (sob demanda).
  const abrirHistorico = useCallback((p: PacienteLeito) => {
    if (!municipioAtivo?.id) return;
    setHistoricoRef(p);
    setHistorico(null);
    setHistoricoErro(null);
    setHistoricoLoading(true);
    saudeService
      .getOcupacaoLeitosHistorico(municipioAtivo.id, { internacaoId: p.internacao_id })
      .then((d: HistoricoPaciente) => setHistorico(d))
      .catch((err: any) => setHistoricoErro(err?.response?.data?.message || 'Erro ao carregar o histórico do paciente.'))
      .finally(() => setHistoricoLoading(false));
  }, [municipioAtivo?.id]);

  const fecharHistorico = useCallback(() => {
    setHistoricoRef(null);
    setHistorico(null);
    setHistoricoErro(null);
  }, []);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um município para visualizar a ocupação de leitos.</Alert></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KingBedIcon color="primary" /> Ocupação de Leitos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unidadeSelecionada
              ? <>Unidade: <strong>{unidadeSelecionada.nome}</strong>{salaSelecionada ? <> • Sala: <strong>{salaSelecionada.nomsala}</strong></> : ''}</>
              : 'Todas as unidades do município'}
            {' '}• Estado atual dos leitos (tempo real)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {ultimaAtualizacao ? `Atualizado às ${ultimaAtualizacao.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </Typography>
            <Typography variant="caption" color="text.disabled">Próxima atualização em {segundos}s</Typography>
          </Box>
          <Tooltip title="Atualizar agora">
            <span>
              <IconButton onClick={() => fetchData(false)} disabled={loading} color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Filtros: sala + periodo (movimentacao) */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Autocomplete
            size="small"
            options={salas}
            getOptionLabel={(o) => o.nomsala}
            value={salaSelecionada}
            onChange={(_, v) => setSalaSelecionada(v)}
            disabled={!unidadeSelecionada}
            loading={salasLoading}
            isOptionEqualToValue={(o, v) => o.id_sala === v.id_sala}
            sx={{ width: 240 }}
            renderInput={(params) => (
              <TextField {...params} label="Sala"
                placeholder={unidadeSelecionada ? 'Todas as salas' : 'Selecione a unidade'} />
            )}
          />
          <TextField
            size="small" type="date" label="Período (início)"
            value={inicio} onChange={(e) => setInicio(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
          />
          <TextField
            size="small" type="date" label="Período (fim)"
            value={fim} onChange={(e) => setFim(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 180 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>
            {unidadeSelecionada
              ? 'O período afeta apenas as métricas de movimentação (internações, altas e permanência).'
              : 'Selecione uma Unidade de Saúde (acima) para filtrar por sala. O período afeta apenas a movimentação.'}
          </Typography>
        </CardContent>
      </Card>

      {naoConfigurado && <PainelNaoConfigurado />}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => fetchData(false)}>Tentar novamente</Button>}>{error}</Alert>}

      {/* KPIs — estado atual */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><KpiCard label="Total de leitos" value={(totais?.total_leitos ?? 0).toLocaleString('pt-BR')} icon={<KingBedIcon />} color="#1976d2" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Ocupados" value={(totais?.ocupados ?? 0).toLocaleString('pt-BR')} icon={<HotelIcon />} color="#e53935" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Disponíveis" value={(totais?.disponiveis ?? 0).toLocaleString('pt-BR')} icon={<EventAvailableIcon />} color="#2e7d32" loading={loading} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label="Taxa de ocupação" value={`${(totais?.taxa_ocupacao ?? 0).toLocaleString('pt-BR')}%`} icon={<PercentIcon />} color="#7b1fa2" loading={loading} /></Grid>
      </Grid>

      {/* Chips secundarios: bloqueados / reservados / salas */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Chip size="small" variant="outlined" icon={<BlockIcon sx={{ fontSize: 16 }} />} label={`Bloqueados: ${totais?.bloqueados ?? 0}`} />
        <Chip size="small" variant="outlined" color="warning" icon={<BookmarkIcon sx={{ fontSize: 16 }} />} label={`Reservados: ${totais?.reservados ?? 0}`} />
        <Chip size="small" variant="outlined" label={`Salas: ${totais?.salas ?? 0}`} />
        {mostrarUnidade && <Chip size="small" variant="outlined" label={`Unidades: ${totais?.unidades ?? 0}`} />}
      </Box>

      {/* Movimentacao no periodo */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Movimentação no período <Typography component="span" variant="caption" color="text.secondary">({data?.periodo?.inicio} a {data?.periodo?.fim})</Typography>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}><KpiCard label="Internações" value={(mov?.internacoes ?? 0).toLocaleString('pt-BR')} icon={<LoginIcon />} color="#1565c0" loading={loading} /></Grid>
            <Grid item xs={12} md={4}><KpiCard label="Altas" value={(mov?.altas ?? 0).toLocaleString('pt-BR')} icon={<LogoutIcon />} color="#00838f" loading={loading} /></Grid>
            <Grid item xs={12} md={4}><KpiCard label="Permanência média (dias)" value={(mov?.permanencia_media_dias ?? 0).toLocaleString('pt-BR')} icon={<HourglassEmptyIcon />} color="#ef6c00" loading={loading} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Por unidade (apenas quando nenhuma unidade selecionada) */}
      {mostrarUnidade && (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Ocupação por unidade</Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={160} />
            ) : porUnidade.length ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Unidade</TableCell>
                      <TableCell align="center">Leitos</TableCell>
                      <TableCell align="center">Ocupados</TableCell>
                      <TableCell align="center">Disponíveis</TableCell>
                      <TableCell align="right">Taxa de ocupação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {porUnidade.map((u) => (
                      <TableRow key={u.codunidade} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.nome}</TableCell>
                        <TableCell align="center">{u.total}</TableCell>
                        <TableCell align="center">{u.ocupados}</TableCell>
                        <TableCell align="center">{u.disponiveis}</TableCell>
                        <TableCell align="right" sx={{ minWidth: 160 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                            <LinearProgress variant="determinate" value={Math.min(u.taxa_ocupacao, 100)} sx={{ width: 80, height: 8, borderRadius: 4 }} />
                            <Typography variant="body2" fontWeight={600}>{u.taxa_ocupacao}%</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" variant="body2">Nenhum leito encontrado.</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ocupacao por sala (acomodacao) */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Ocupação por sala</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Clique numa sala para ver o tempo médio de permanência e os pacientes internados.
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : porSala.length ? (
            <Grid container spacing={2}>
              {porSala.map((s) => (
                <Grid item xs={12} md={6} key={s.acomodacao_id}>
                  <Box
                    onClick={() => abrirSala(s)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirSala(s); } }}
                    sx={{
                      p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`,
                      cursor: 'pointer', transition: 'background-color .15s, border-color .15s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), borderColor: theme.palette.primary.main },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>{s.sala}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {s.bloco}{s.clinica ? ` • ${s.clinica}` : ''}{mostrarUnidade ? ` • ${s.unidade_nome}` : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={700}>{s.taxa_ocupacao}%</Typography>
                        <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </Box>
                    </Box>
                    <LinearProgress variant="determinate" value={Math.min(s.taxa_ocupacao, 100)} sx={{ my: 1, height: 8, borderRadius: 4 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <Chip size="small" variant="outlined" label={`${s.total} leitos`} />
                      <Chip size="small" color="error" variant="outlined" label={`${s.ocupados} ocupados`} />
                      <Chip size="small" color="success" variant="outlined" label={`${s.disponiveis} disponíveis`} />
                      {s.bloqueados > 0 && <Chip size="small" variant="outlined" label={`${s.bloqueados} bloq.`} />}
                      {s.reservados > 0 && <Chip size="small" color="warning" variant="outlined" label={`${s.reservados} reserv.`} />}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary" variant="body2">Nenhum leito para os filtros selecionados.</Typography>
          )}
        </CardContent>
      </Card>

      {/* Grid de leitos (estado de cada leito, sem dados de paciente) */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Leitos</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(SITUACOES).map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: v.hex }} />
                  <Typography variant="caption" color="text.secondary">{v.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {loading ? (
            <Skeleton variant="rectangular" height={140} />
          ) : leitosPorSala.length ? (
            leitosPorSala.map((g, gi) => (
              <Box key={gi} sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {g.sala}{mostrarUnidade ? ` • ${g.unidade_nome}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {g.itens.map((l) => {
                    const c = corSituacao(l.situacao);
                    return (
                      <Tooltip key={l.leito_id} title={`${l.situacao_nome}${l.indisolamento === 'S' ? ' • Isolamento' : ''}`}>
                        <Box sx={{
                          minWidth: 84, px: 1.5, py: 1, borderRadius: 2,
                          border: `1px solid ${alpha(c.hex, 0.5)}`, bgcolor: alpha(c.hex, 0.1),
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <KingBedIcon sx={{ fontSize: 18, color: c.hex }} />
                            <Typography variant="body2" fontWeight={700}>{l.leito}</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: c.hex, fontWeight: 600 }}>{c.label}</Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">Nenhum leito para os filtros selecionados.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Chip size="small" variant="outlined" sx={{ mt: 2 }}
        label={`Ocupação em tempo real • Situação do leito (Disponível / Ocupado / Bloqueado / Reservado) • Clique numa sala para detalhar os pacientes internados${unidadeSelecionada ? ` • ${unidadeSelecionada.nome}` : ''}`} />

      {/* Detalhe da sala (POC 16.10 tempo médio + 16.11 lista de pacientes internados) */}
      <Dialog
        open={!!salaDetalheRef}
        onClose={fecharSala}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1, pr: 6,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}>
          <KingBedIcon sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>{salaDetalheRef?.sala}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {salaDetalheRef?.bloco}
              {salaDetalheRef?.clinica ? ` • ${salaDetalheRef.clinica}` : ''}
              {salaDetalheRef?.unidade_nome ? ` • ${salaDetalheRef.unidade_nome}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={fecharSala} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          {detalheLoading ? (
            <Box>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            </Box>
          ) : detalheErro ? (
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={() => salaDetalheRef && abrirSala(salaDetalheRef)}>Tentar novamente</Button>
            }>{detalheErro}</Alert>
          ) : (
            <>
              {/* 16.10 — tempo médio de permanência (ao vivo) */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <KpiCard
                    label={`Tempo médio de permanência • ${detalhe?.ocupados ?? 0} paciente${(detalhe?.ocupados ?? 0) === 1 ? '' : 's'} internado${(detalhe?.ocupados ?? 0) === 1 ? '' : 's'}`}
                    value={detalhe && detalhe.ocupados > 0 ? formatarPermanencia(detalhe.permanencia_media_seg) : '—'}
                    icon={<HourglassEmptyIcon />} color="#ef6c00" loading={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <KpiCard
                    label="Leitos ocupados (com paciente)"
                    value={(detalhe?.ocupados ?? 0).toLocaleString('pt-BR')}
                    icon={<HotelIcon />} color="#e53935" loading={false}
                  />
                </Grid>
              </Grid>

              {/* 16.11 — lista de pacientes (anonimizada: leito, sexo, idade, tempo) */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>Pacientes internados</Typography>
              {detalhe && detalhe.pacientes.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Clique em um paciente para ver o histórico do atendimento.
                </Typography>
              )}
              {detalhe && detalhe.pacientes.length > 0 ? (
                <TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Leito</TableCell>
                        <TableCell>Paciente</TableCell>
                        <TableCell>Prontuário</TableCell>
                        <TableCell>Sexo</TableCell>
                        <TableCell align="center">Idade</TableCell>
                        <TableCell>Internado em</TableCell>
                        <TableCell align="right">Tempo de permanência</TableCell>
                        <TableCell align="center">Histórico</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalhe.pacientes.map((p) => (
                        <TableRow
                          key={p.leito_id}
                          hover
                          onClick={() => abrirHistorico(p)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirHistorico(p); } }}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <KingBedIcon sx={{ fontSize: 18, color: '#e53935' }} />
                              <Typography variant="body2" fontWeight={600}>{p.leito}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{p.nome || '—'}</Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {p.numprontuario != null ? p.numprontuario : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined"
                              color={p.sexo === 'M' ? 'info' : p.sexo === 'F' ? 'secondary' : 'default'}
                              label={labelSexo(p.sexo)} />
                          </TableCell>
                          <TableCell align="center">{p.idade != null ? `${p.idade} anos` : '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.dt_internacao_fmt}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {formatarPermanencia(p.permanencia_seg)}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title="Ver histórico do atendimento">
                              <HistoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main, verticalAlign: 'middle' }} />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">Nenhum paciente internado nesta sala no momento.</Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={fecharSala} variant="outlined" size="small">Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Historico do atendimento do paciente internado (POC 16.16) — anonimizado */}
      <Dialog
        open={!!historicoRef}
        onClose={fecharHistorico}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1, pr: 6,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}>
          <HistoryIcon sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {historicoRef?.nome || historico?.nome || 'Histórico do atendimento'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {(() => {
                const pront = historicoRef?.numprontuario ?? historico?.numprontuario;
                const leito = historicoRef?.leito ?? historico?.leito;
                return [pront != null ? `Prontuário ${pront}` : null, leito ? `Leito ${leito}` : null]
                  .filter(Boolean).join(' • ');
              })()}
            </Typography>
          </Box>
          <IconButton onClick={fecharHistorico} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          {historicoLoading ? (
            <Box>
              <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Box>
          ) : historicoErro ? (
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={() => historicoRef && abrirHistorico(historicoRef)}>Tentar novamente</Button>
            }>{historicoErro}</Alert>
          ) : (
            <>
              {/* Resumo da internacao */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                <Chip size="small" variant="outlined"
                  color={historico?.situacao === 'Internado' ? 'error' : 'success'}
                  label={historico?.situacao || '—'} />
                <Chip size="small" variant="outlined"
                  color={historico?.sexo === 'M' ? 'info' : historico?.sexo === 'F' ? 'secondary' : 'default'}
                  label={labelSexo(historico?.sexo ?? null)} />
                {historico?.idade != null && (
                  <Chip size="small" variant="outlined" label={`${historico.idade} anos`} />
                )}
                <Chip size="small" variant="outlined" icon={<EventAvailableIcon />}
                  label={`Internado em ${historico?.dt_internacao_fmt || '—'}`} />
                <Chip size="small" variant="outlined" icon={<HourglassEmptyIcon />}
                  label={formatarPermanencia(historico?.permanencia_seg || 0)} />
              </Box>

              {historico && historico.eventos.length > 0 ? (
                <Box>
                  {historico.eventos.map((ev, idx) => {
                    const ultimo = idx === historico.eventos.length - 1;
                    const cfg =
                      ev.tipo === 'admissao'
                        ? { icon: <LoginIcon sx={{ fontSize: 16 }} />, hex: theme.palette.primary.main }
                        : ev.tipo === 'alta'
                        ? { icon: <LogoutIcon sx={{ fontSize: 16 }} />, hex: '#2e7d32' }
                        : { icon: <MonitorHeartIcon sx={{ fontSize: 16 }} />, hex: '#ef6c00' };
                    return (
                      <Box key={idx} sx={{ display: 'flex', gap: 1.5 }}>
                        {/* Trilho vertical */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{
                            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(cfg.hex, 0.12), color: cfg.hex,
                          }}>
                            {cfg.icon}
                          </Box>
                          {!ultimo && <Box sx={{ flex: 1, width: 2, bgcolor: theme.palette.divider, my: 0.5 }} />}
                        </Box>
                        {/* Conteudo do evento */}
                        <Box sx={{ pb: ultimo ? 0 : 2.5, minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">{ev.data_hora_fmt}</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>{ev.titulo}</Typography>
                          {ev.detalhes && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                              {ev.detalhes}
                            </Typography>
                          )}
                          {ev.sinais && ev.sinais.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                              {ev.sinais.map((s, i) => (
                                <Chip key={i} size="small" variant="outlined"
                                  label={`${s.rotulo}: ${s.valor}`}
                                  sx={{ height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info">Sem registros de evolução para esta internação.</Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={fecharHistorico} variant="outlined" size="small">Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SaudeOcupacaoLeitos;
